import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  MessageCircle,
  Send,
  Paperclip,
  CheckCircle2,
  Check,
  Circle,
  Loader2,
  Users,
  ArrowRight,
  Link as LinkIcon,
  Sparkles,
  SkipForward,
  ListChecks,
  Wand2,
  BellRing,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SetupAddressField from "./SetupAddressField";
import SetupPointsField from "./SetupPointsField";

// ── UI strings per language. Backend handles AI replies in any language;
// these are the static labels in the chrome only. Falls back to EN when
// a key is missing for the active language.
const I18N = {
  en: {
    title: "Setup chat",
    loading: "Loading…",
    aiThinking: "AI is thinking…",
    noMessages: "No messages yet.",
    placeholderSales: "Tell me what you know about the project…",
    placeholderClient: "Type your reply…",
    inputHint: "Enter to send · Shift+Enter for a new line · attach files with the clip icon to extract or pin them.",
    progress: "Progress",
    requiredLabel: "Required:",
    sentPrefix: "Sent",
    sendToClient: "Send to client",
    notifyManager: "Notify manager",
    notifyManagerSuccess: (n) => `Notified ${n} to review`,
    notifyManagerSelf: "You are the project manager on this one",
    notifyManagerFailed: "Could not notify manager",
    backToSales: "← Back to sales drafting",
    allRequiredCollected: "All required fields collected",
    confirmSendIncomplete: "Not all required fields are filled. Send to client anyway?",
    sendSuccess: "Sent to client",
    revertSuccess: "Reverted to sales drafting",
    languageUpdated: "Chat language updated",
    languageTitle: "Chat language",
    copyLink: "Copy client link",
    copyLinkSuccess: "Link copied — share with client",
    attach: "Attach…",
    couldNotLoad: "Could not load setup chat",
    couldNotSend: "Could not send",
    couldNotChangeStage: "Could not change stage",
    couldNotChangeLanguage: "Could not change language",
    fileTooLarge: "File too large (max 30MB)",
    uploadFailed: "Upload failed",
    attachedToast: (n) => `Attached: ${n}`,
    stageSales: "Sales drafting",
    stageClient: "Awaiting client",
    stageCompleted: "Completed",
    settings: "Customize questions: Settings → Setup Chat Flows",
    autoSaveHint: "Your answers save automatically as you fill them — no Save button needed.",
    handoffLabel: "When you're ready:",
    savedTick: "Saved",
    intakeTitle: "Got existing materials? Let me read them first.",
    intakeBlurb: "Upload a brochure, deck or DOC, or paste a project website — I'll extract everything I can and pre-fill the form. You'll just confirm what I found.",
    intakeUploadFile: "Upload brief (PDF / Word / image)",
    intakePasteUrl: "Paste website URL",
    intakeSkip: "Skip — I'll fill it manually",
    intakeUrlPlaceholder: "https://your-project-site.com",
    intakeUrlSubmit: "Read this site",
    flowLabel: "Flow",
    flowDefault: "Project Setup",
    flowTooltip: (id) => `Flow: ${id} — manage in Settings → Setup Chat Flows`,
    statusCursor: "Currently asking this — see chat",
    statusVerified: "Confirmed by user",
    statusFilled: "Filled, not yet confirmed",
    statusSkipped: "User skipped this optional field",
    statusUnasked: "Not asked yet",
    skippedSuffix: "skipped",
    emptyValue: "(empty)",
    confirmedHint: "✓ confirmed by user",
    awaitingHint: "◐ filled, awaiting confirmation",
    fieldKey: "field",
    skippedNote: "Skipped by user.",
    askingNowNote: "AI is asking this question now — see chat.",
    notAskedNote: "Not asked yet.",
    answerInline: "Answer here",
    saveBtn: "Save",
    confirmBtn: "Confirm",
    suggestedHint: "Pre-filled from project data — check and confirm",
    cancelBtn: "Cancel",
    skipFieldBtn: "Skip",
    chatSkip: "Skip this question",
    viewChat: "Chat",
    viewForm: "Form",
    viewChatHint: "Talk it through — the assistant asks one question at a time and can read a brief for you.",
    viewFormHint: "All questions on one screen. Same answers, same saves — faster if you already know them.",
    gkInvite: "Share what you have, top to bottom — the more from the top, the faster we build.",
    gkEssentialsDone: "Essentials in — that's enough for us to start. Anything below is a bonus.",
    gkDoneAll: "Everything's in. Your project is ready to build.",
    gkEssential: "Essential",
    gkHelpful: "Helpful",
    gkOptional: "Optional",
    gkImprovise: (n) => `${n} of these we can produce ourselves if you don't have them.`,
    gkImproviseField: "We can produce this ourselves if you don't have it",
    addrFind: "Find",
    addrDragHint: "Drag the pin to fine-tune the exact spot.",
    addrNoPin: "We couldn't place this on the map — check the address.",
    pointsLoading: "Finding nearby places…",
    pointsNeedAddress: "Fill in the project address first — we place the map from it.",
    pointsHint: "Tap a pin to include it, or click the map to add your own.",
    pointsAddPrompt: "Name this place (e.g. Central School)",
    pointsSave: (n) => `Save ${n} point${n === 1 ? "" : "s"}`,
    pointsSuggested: (n) => `${n} suggested nearby`,
    pointsCount: (n) => `${n} point${n === 1 ? "" : "s"}`,
    gkBannerTitle: "Your part of the setup",
    gkBannerBody: "Your team has done their side — to bring the project to life we need these from you. Give what you have from the top; the rest is optional and we can handle it.",
    blocksPool: "you",
    blocksPoolHint: "Your field as the sales manager — the project can't be handed to a PM until it's filled, and it can't be skipped.",
    clearAnswerBtn: "Clear",
    clearAnswerHint: "Delete this answer",
    editLinkPlaceholder: "edit the link (https://…)",
    replaceFile: "Replace",
    linkPlaceholder: "or paste a link (https://…)",
    saveLinkBtn: "Save link",
    editAnswerBtn: "Edit",
    saved: "Saved",
    saving: "Saving…",
    couldNotSave: "Could not save",
    selectOption: "Select…",
    yesLabel: "Yes",
    noLabel: "No",
  },
  uk: {
    title: "Чат налаштування",
    loading: "Завантаження…",
    aiThinking: "AI думає…",
    noMessages: "Поки що немає повідомлень.",
    placeholderSales: "Розкажіть, що знаєте про проєкт…",
    placeholderClient: "Введіть відповідь…",
    inputHint: "Enter — надіслати · Shift+Enter — новий рядок · скріпка для файлів.",
    progress: "Прогрес",
    requiredLabel: "Обов'язкові:",
    sentPrefix: "Надіслано",
    sendToClient: "Надіслати клієнту",
    notifyManager: "Повідомити менеджера",
    notifyManagerSuccess: (n) => `Повідомлено ${n} на перевірку`,
    notifyManagerSelf: "Ви — менеджер цього проєкту",
    notifyManagerFailed: "Не вдалося повідомити менеджера",
    backToSales: "← Повернути до підготовки",
    allRequiredCollected: "Всі обов'язкові поля заповнені",
    confirmSendIncomplete: "Не всі обов'язкові поля заповнені. Все одно надіслати клієнту?",
    sendSuccess: "Надіслано клієнту",
    revertSuccess: "Повернено до підготовки",
    languageUpdated: "Мову оновлено",
    languageTitle: "Мова чату",
    copyLink: "Копіювати посилання",
    copyLinkSuccess: "Посилання скопійовано — поділіться з клієнтом",
    attach: "Прикріпити…",
    couldNotLoad: "Не вдалося завантажити чат",
    couldNotSend: "Не вдалося надіслати",
    couldNotChangeStage: "Не вдалося змінити стадію",
    couldNotChangeLanguage: "Не вдалося змінити мову",
    fileTooLarge: "Файл завеликий (макс 30 МБ)",
    uploadFailed: "Не вдалося завантажити",
    attachedToast: (n) => `Прикріплено: ${n}`,
    stageSales: "Готує менеджер",
    stageClient: "Очікує клієнта",
    stageCompleted: "Завершено",
    settings: "Питання редагуються у backend/setup_questions.py",
    autoSaveHint: "Відповіді зберігаються автоматично, поки ви заповнюєте — кнопка «Зберегти» не потрібна.",
    handoffLabel: "Коли будете готові:",
    savedTick: "Збережено",
    intakeTitle: "Є вже якісь матеріали по проєкту? Дайте мені — я прочитаю.",
    intakeBlurb: "Завантажте брошуру, презентацію, DOC або вставте посилання на сайт — я витягну все що знайду і попередньо заповню форму. Вам залишиться тільки підтвердити.",
    intakeUploadFile: "Завантажити бриф (PDF / Word / зображення)",
    intakePasteUrl: "Вставити посилання на сайт",
    intakeSkip: "Пропустити — заповню вручну",
    intakeUrlPlaceholder: "https://сайт-проєкту.com",
    intakeUrlSubmit: "Прочитати цей сайт",
    flowLabel: "Флоу",
    flowDefault: "Налаштування проєкту",
    flowTooltip: (id) => `Флоу: ${id} — редагується у Settings → Setup Chat Flows`,
    statusCursor: "Зараз AI запитує це — дивіться чат",
    statusVerified: "Підтверджено користувачем",
    statusFilled: "Заповнено, ще не підтверджено",
    statusSkipped: "Користувач пропустив це необов'язкове поле",
    statusUnasked: "Ще не запитано",
    skippedSuffix: "пропущено",
    emptyValue: "(порожньо)",
    confirmedHint: "✓ підтверджено користувачем",
    awaitingHint: "◐ заповнено, очікує підтвердження",
    fieldKey: "поле",
    skippedNote: "Пропущено користувачем.",
    askingNowNote: "AI запитує це зараз — дивіться чат.",
    notAskedNote: "Ще не запитано.",
    answerInline: "Відповідь",
    saveBtn: "Зберегти",
    confirmBtn: "Підтвердити",
    suggestedHint: "Підставлено з даних проєкту — перевірте й підтвердіть",
    cancelBtn: "Скасувати",
    skipFieldBtn: "Пропустити",
    chatSkip: "Пропустити це питання",
    viewChat: "Чат",
    viewForm: "Форма",
    viewChatHint: "Пройти діалогом — асистент питає по одному й може прочитати бриф за вас.",
    viewFormHint: "Усі питання на одному екрані. Ті самі відповіді — швидше, якщо ви їх уже знаєте.",
    gkInvite: "Дайте що є, зверху вниз — чим більше з верхівки, тим швидше зробимо.",
    gkEssentialsDone: "Головне є — цього вже досить, щоб почати. Усе нижче — бонус.",
    gkDoneAll: "Усе зібрано. Проєкт готовий до роботи.",
    gkEssential: "Головне",
    gkHelpful: "Корисне",
    gkOptional: "Необов'язкове",
    gkImprovise: (n) => `${n} з цього ми можемо зробити самі, якщо у вас немає.`,
    gkImproviseField: "Ми можемо зробити це самі, якщо у вас немає",
    addrFind: "Знайти",
    addrDragHint: "Перетягніть маркер, щоб уточнити місце.",
    addrNoPin: "Не вдалося показати на карті — перевірте адресу.",
    pointsLoading: "Шукаємо місця поруч…",
    pointsNeedAddress: "Спершу заповніть адресу проєкту — карту будуємо від неї.",
    pointsHint: "Торкніться пінки, щоб додати, або клікніть по карті, щоб поставити свою.",
    pointsAddPrompt: "Назвіть це місце (напр. Школа №5)",
    pointsSave: (n) => `Зберегти ${n} ${n === 1 ? "точку" : "точок"}`,
    pointsSuggested: (n) => `${n} поруч запропоновано`,
    pointsCount: (n) => `${n} ${n === 1 ? "точка" : "точок"}`,
    gkBannerTitle: "Ваша частина підготовки",
    gkBannerBody: "Команда зробила свою частину — щоб оживити проєкт, нам потрібно це від вас. Дайте що є зверху; решта необов'язкова, і ми з нею впораємось.",
    blocksPool: "ви",
    blocksPoolHint: "Ваше поле як менеджера — без нього проєкт не можна передати ПМу, і пропустити його не можна.",
    clearAnswerBtn: "Очистити",
    clearAnswerHint: "Видалити цю відповідь",
    editLinkPlaceholder: "змініть посилання (https://…)",
    replaceFile: "Замінити",
    linkPlaceholder: "або вставте посилання (https://…)",
    saveLinkBtn: "Зберегти посилання",
    editAnswerBtn: "Редагувати",
    saved: "Збережено",
    saving: "Зберігаю…",
    couldNotSave: "Не вдалося зберегти",
    selectOption: "Оберіть…",
    yesLabel: "Так",
    noLabel: "Ні",
  },
  ru: {
    title: "Чат настройки",
    loading: "Загрузка…",
    aiThinking: "AI думает…",
    noMessages: "Сообщений пока нет.",
    placeholderSales: "Расскажите, что знаете о проекте…",
    placeholderClient: "Введите ответ…",
    inputHint: "Enter — отправить · Shift+Enter — новая строка · скрепка для файлов.",
    progress: "Прогресс",
    requiredLabel: "Обязательные:",
    sentPrefix: "Отправлено",
    sendToClient: "Отправить клиенту",
    notifyManager: "Уведомить менеджера",
    notifyManagerSuccess: (n) => `Уведомлён(а) ${n} на проверку`,
    notifyManagerSelf: "Вы — менеджер этого проекта",
    notifyManagerFailed: "Не удалось уведомить менеджера",
    backToSales: "← Вернуть к подготовке",
    allRequiredCollected: "Все обязательные поля собраны",
    confirmSendIncomplete: "Не все обязательные поля заполнены. Отправить клиенту?",
    sendSuccess: "Отправлено клиенту",
    revertSuccess: "Возвращено к подготовке",
    languageUpdated: "Язык обновлён",
    languageTitle: "Язык чата",
    copyLink: "Скопировать ссылку",
    copyLinkSuccess: "Ссылка скопирована — поделитесь с клиентом",
    attach: "Прикрепить…",
    couldNotLoad: "Не удалось загрузить чат",
    couldNotSend: "Не удалось отправить",
    couldNotChangeStage: "Не удалось сменить стадию",
    couldNotChangeLanguage: "Не удалось сменить язык",
    fileTooLarge: "Файл слишком большой (макс 30 МБ)",
    uploadFailed: "Загрузка не удалась",
    attachedToast: (n) => `Прикреплено: ${n}`,
    stageSales: "Готовит менеджер",
    stageClient: "Ожидает клиента",
    stageCompleted: "Завершено",
    settings: "Вопросы редактируются в backend/setup_questions.py",
    autoSaveHint: "Ответы сохраняются автоматически по мере заполнения — кнопка «Сохранить» не нужна.",
    handoffLabel: "Когда будете готовы:",
    savedTick: "Сохранено",
    intakeTitle: "Есть уже какие-то материалы по проекту? Дайте мне — я прочитаю.",
    intakeBlurb: "Загрузите брошюру, презентацию, DOC или вставьте ссылку на сайт — я извлеку всё что найду и предзаполню форму. Вам останется только подтвердить.",
    intakeUploadFile: "Загрузить бриф (PDF / Word / изображение)",
    intakePasteUrl: "Вставить ссылку на сайт",
    intakeSkip: "Пропустить — заполню вручную",
    intakeUrlPlaceholder: "https://сайт-проекта.com",
    intakeUrlSubmit: "Прочитать этот сайт",
    flowLabel: "Флоу",
    flowDefault: "Настройка проекта",
    flowTooltip: (id) => `Флоу: ${id} — редактируется в Settings → Setup Chat Flows`,
    statusCursor: "Сейчас AI спрашивает об этом — смотрите чат",
    statusVerified: "Подтверждено пользователем",
    statusFilled: "Заполнено, ещё не подтверждено",
    statusSkipped: "Пользователь пропустил это необязательное поле",
    statusUnasked: "Ещё не спросили",
    skippedSuffix: "пропущено",
    emptyValue: "(пусто)",
    confirmedHint: "✓ подтверждено пользователем",
    awaitingHint: "◐ заполнено, ожидает подтверждения",
    fieldKey: "поле",
    skippedNote: "Пропущено пользователем.",
    askingNowNote: "AI спрашивает об этом сейчас — смотрите чат.",
    notAskedNote: "Ещё не спросили.",
    answerInline: "Ответ",
    saveBtn: "Сохранить",
    confirmBtn: "Подтвердить",
    suggestedHint: "Подставлено из данных проекта — проверьте и подтвердите",
    cancelBtn: "Отмена",
    skipFieldBtn: "Пропустить",
    chatSkip: "Пропустить этот вопрос",
    viewChat: "Чат",
    viewForm: "Форма",
    viewChatHint: "Пройти диалогом — ассистент спрашивает по одному и может прочитать бриф за вас.",
    viewFormHint: "Все вопросы на одном экране. Те же ответы — быстрее, если вы их уже знаете.",
    gkInvite: "Дайте что есть, сверху вниз — чем больше с верхушки, тем быстрее сделаем.",
    gkEssentialsDone: "Главное есть — этого уже достаточно, чтобы начать. Всё ниже — бонус.",
    gkDoneAll: "Всё собрано. Проект готов к работе.",
    gkEssential: "Главное",
    gkHelpful: "Полезное",
    gkOptional: "Необязательное",
    gkImprovise: (n) => `${n} из этого мы можем сделать сами, если у вас нет.`,
    gkImproviseField: "Мы можем сделать это сами, если у вас нет",
    addrFind: "Найти",
    addrDragHint: "Перетащите маркер, чтобы уточнить место.",
    addrNoPin: "Не удалось показать на карте — проверьте адрес.",
    pointsLoading: "Ищем места рядом…",
    pointsNeedAddress: "Сначала заполните адрес проекта — карту строим от него.",
    pointsHint: "Нажмите на пин, чтобы добавить, или кликните по карте, чтобы поставить свою точку.",
    pointsAddPrompt: "Назовите это место (напр. Школа №5)",
    pointsSave: (n) => `Сохранить ${n} ${n === 1 ? "точку" : "точек"}`,
    pointsSuggested: (n) => `${n} рядом предложено`,
    pointsCount: (n) => `${n} ${n === 1 ? "точка" : "точек"}`,
    gkBannerTitle: "Ваша часть подготовки",
    gkBannerBody: "Команда сделала свою часть — чтобы оживить проект, нам нужно это от вас. Дайте что есть сверху; остальное необязательно, и мы с ним справимся.",
    blocksPool: "вы",
    blocksPoolHint: "Ваше поле как менеджера — без него проект нельзя передать ПМу, и пропустить его нельзя.",
    clearAnswerBtn: "Очистить",
    clearAnswerHint: "Удалить этот ответ",
    editLinkPlaceholder: "измените ссылку (https://…)",
    replaceFile: "Заменить",
    linkPlaceholder: "или вставьте ссылку (https://…)",
    saveLinkBtn: "Сохранить ссылку",
    editAnswerBtn: "Редактировать",
    saved: "Сохранено",
    saving: "Сохраняю…",
    couldNotSave: "Не удалось сохранить",
    selectOption: "Выберите…",
    yesLabel: "Да",
    noLabel: "Нет",
  },
};
const T = (lang, key, ...args) => {
  const dict = I18N[lang] || I18N.en;
  const v = dict[key] ?? I18N.en[key];
  return typeof v === "function" ? v(...args) : v;
};

/**
 * Per-service AI setup chat — two-stage flow.
 *
 *   Stage 'sales'      → sales rep talks to GPT-4o to assemble what they know.
 *                         "Send to client" hands off to the client.
 *   Stage 'client'     → the client (logged in as role=client) reviews what sales
 *                         filled, AI walks them through confirmation, then collects
 *                         remaining required fields.
 *   Stage 'completed'  → all required fields filled.
 *
 * State: backend stores setup_chat_messages, setup_responses, setup_stage on
 * the service doc. Schema is fetched alongside in /sales-services/{id}/setup-chat.
 */
export default function SetupChatButton({
  taskId,
  serviceName,
  serviceType,
  canEdit,
  autoOpen = false,
  variant = "icon",
  // Render the chat straight into the page (no launcher, no modal overlay) — used
  // in the client cabinet so the setup lives in the page, not behind a button.
  inline = false,
  // Fired after the modal closes when anything was written, so the host page can
  // refresh derived state (e.g. the PM-pool gate banner) without a full reload.
  onSaved,
}) {
  const { user } = useAuth();
  const isClient = user?.role === "client";

  // Inline mode is always "open" so its data loads on mount.
  const [open, setOpen] = useState(inline);

  // Auto-open when navigating with ?service=<id> on the client portal.
  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);
  const [state, setState] = useState(null); // backend status payload
  // Set by any write in this session; consumed by onSaved when the modal closes.
  const dirtyRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);
  // Intake panel: visible at the very start of a sales-stage chat. Lets the
  // user upload a brief / paste a URL so the AI can pre-fill the form. The
  // panel auto-hides as soon as real progress happens or the user dismisses.
  const [intakeDismissed, setIntakeDismissed] = useState(false);
  const [intakeUrlInput, setIntakeUrlInput] = useState("");
  const [intakeUrlOpen, setIntakeUrlOpen] = useState(false);
  // Sidebar: which field's full answer is currently expanded (single open at a time).
  const [expandedField, setExpandedField] = useState(null);
  // "chat" | "form". The AI chat is the primary experience for everyone —
  // one flow to explain, and it's what the client sees. Form view is the same
  // fields on one wide screen with every row open: a shortcut for someone who
  // already knows the answers (typically our own managers) rather than a
  // separate UX. Deliberately not role-gated — clients may use it too.
  const [view, setView] = useState("chat");
  const isForm = view === "form";

  const stage = state?.stage || "sales";
  const lang = state?.language || "en";
  const t = (key, ...args) => T(lang, key, ...args);

  // Intake panel visibility: only at start of sales drafting, when nothing is
  // filled yet, no real conversation has happened, no client mode, not completed.
  const intakePanelVisible = (() => {
    if (stage !== "sales") return false;
    if (intakeDismissed) return false;
    if (!canEdit) return false;
    if (isClient) return false;
    const responses = state?.responses || {};
    if (Object.keys(responses).length > 0) return false;
    const msgCount = (state?.messages || []).length;
    // Allow only the lone auto-seeded intro (1 message) — not the post-handoff
    // bubble or any back-and-forth.
    return msgCount <= 1;
  })();

  // ── Load chat state when modal opens ───────────────────────────────────
  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/setup-chat`);
      setState(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`${t("couldNotLoad")}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [state?.messages?.length, sending]);

  // ── Send a message ──────────────────────────────────────────────────────
  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? draft).trim();
    if (!text && pendingAttachments.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/setup-chat/message`, {
        message: text,
        attachments: pendingAttachments,
      });
      setState(data);
      dirtyRef.current = true;
      setDraft("");
      setPendingAttachments([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotSend"));
    } finally {
      setSending(false);
    }
  };

  // ── Upload a file (optionally pinned to a field) ────────────────────────
  const triggerUpload = (field = null) => {
    fileRef.current.dataset.field = field || "";
    fileRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error(t("fileTooLarge"));
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const field = fileRef.current.dataset.field || "";
      if (field) formData.append("field", field);
      const { data } = await api.post(
        `/tasks/${taskId}/setup-chat/file`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPendingAttachments((p) => [...p, data]);
      toast.success(t("attachedToast", data.file_name));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("uploadFailed"));
    } finally {
      setUploadingFile(false);
    }
  };

  // ── Stage transitions ───────────────────────────────────────────────────
  const setStage = async (newStage) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/setup-chat/stage`, {
        stage: newStage,
      });
      setState(data);
      dirtyRef.current = true;
      if (newStage === "client") toast.success(t("sendSuccess"));
      else if (newStage === "sales") toast.success(t("revertSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotChangeStage"));
    }
  };

  // ── Notify the project manager: setup ready to review ───────────────────
  const [notifyingManager, setNotifyingManager] = useState(false);
  const notifyManager = async () => {
    setNotifyingManager(true);
    try {
      const { data } = await api.post(
        `/tasks/${taskId}/setup-chat/notify-manager`,
      );
      if (data?.self) {
        toast.info(t("notifyManagerSelf"));
      } else {
        toast.success(t("notifyManagerSuccess", data?.notified_name || ""));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("notifyManagerFailed"));
    } finally {
      setNotifyingManager(false);
    }
  };

  // ── Language ────────────────────────────────────────────────────────────
  const setLanguage = async (code) => {
    if (!code || code === state?.language) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/setup-chat/language`, {
        language: code,
      });
      setState(data);
      toast.success(t("languageUpdated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotChangeLanguage"));
    }
  };

  // ── Direct save of a single field from the sidebar (no LLM) ─────────────
  // The sidebar shows an inline edit form for any non-file question. The
  // user can type/select directly and we commit straight to the responses
  // map + verified set on the backend, bypassing the chat conversation.
  const [savingField, setSavingField] = useState(null); // field key while saving

  const saveField = async (field, value, opts = {}) => {
    setSavingField(field);
    try {
      const { data } = await api.post(
        `/tasks/${taskId}/setup-chat/save-field`,
        { field, value, skipped: !!opts.skipped },
      );
      setState(data);
      dirtyRef.current = true;
      // Auto-collapse the row on a successful save unless the caller asks
      // to keep it open (e.g. for a quick second edit).
      if (!opts.keepOpen) setExpandedField(null);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotSave"));
    } finally {
      setSavingField(null);
    }
  };

  // Persist the chosen points of interest for a `points` field.
  const savePoints = async (field, points) => {
    setSavingField(field);
    try {
      const { data } = await api.post(
        `/tasks/${taskId}/setup-chat/save-points`,
        { field, points },
      );
      setState(data);
      dirtyRef.current = true;
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotSave"));
    } finally {
      setSavingField(null);
    }
  };

  // Persist a dragged map-marker position for an address field (coords only).
  const saveCoords = async (field, lat, lng) => {
    setSavingField(field);
    try {
      const { data } = await api.post(
        `/tasks/${taskId}/setup-chat/save-field`,
        { field, lat, lng },
      );
      setState(data);
      dirtyRef.current = true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("couldNotSave"));
    } finally {
      setSavingField(null);
    }
  };

  // ── Copy link for client ────────────────────────────────────────────────
  const copyClientLink = async () => {
    const url = `${window.location.origin}/client-portal?task=${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("copyLinkSuccess"));
    } catch {
      toast.error("Copy failed");
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────
  const schema = state?.schema || [];
  const responses = state?.responses || {};
  const messages = state?.messages || [];
  const requiredFilledRatio = state
    ? `${state.required_filled}/${state.required_count}`
    : "0/0";
  const totalRatio = state ? `${state.total_filled}/${state.total_count}` : "0/0";
  const progressPct = state && state.total_count
    ? Math.round((state.total_filled * 100) / state.total_count)
    : 0;

  // ── Client priority gradient (the gatekeeper's progress) ──────────────────
  // The client's part is ranked, not required. Reward top-down filling: once the
  // essentials are in, we can start — that's the message that lands, even with
  // the bottom empty. `client_progress` comes from the backend per tier.
  const cp = state?.client_progress || {};
  const tier = (k) => cp[k] || { filled: 0, total: 0 };
  const essential = tier("essential");
  const helpful = tier("helpful");
  const optional = tier("optional");
  const clientTotal = essential.total + helpful.total + optional.total;
  const clientFilled = essential.filled + helpful.filled + optional.filled;
  const essentialsDone = essential.total > 0 && essential.filled >= essential.total;
  const allClientDone = clientTotal > 0 && clientFilled >= clientTotal;
  const clientPct = clientTotal ? Math.round((clientFilled * 100) / clientTotal) : 0;
  // The headline shifts with momentum: invite → essentials milestone → finish.
  const gateHeadline = allClientDone
    ? t("gkDoneAll")
    : essentialsDone
    ? t("gkEssentialsDone")
    : t("gkInvite");
  const improvisableMissing = state?.improvisable_missing || [];

  const sections = useMemo(() => {
    const groups = {};
    schema.forEach((q) => {
      if (!groups[q.section]) groups[q.section] = [];
      groups[q.section].push(q);
    });
    return Object.entries(groups);
  }, [schema]);

  const StageBadge = () => {
    const map = {
      sales: { label: t("stageSales"), color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
      client: { label: t("stageClient"), color: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" },
      completed: { label: t("stageCompleted"), color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    };
    const m = map[stage] || map.sales;
    return (
      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${m.color}`}>
        {m.label}
      </span>
    );
  };

  const formatVal = (v) => {
    if (!v) return "";
    if (Array.isArray(v)) return t("pointsCount", v.length);
    if (typeof v === "object" && v.file_name) return `${v.is_link ? "🔗" : "📎"} ${v.file_name}`;
    const s = String(v);
    return s.length > 60 ? s.slice(0, 57) + "…" : s;
  };

  // The header + gatekeeper + chat/form grid, shared between the modal and the
  // inline (in-page) rendering.
  const chatShell = (
    <>
          <div className="px-5 py-3 border-b border-zinc-800 flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 text-zinc-50 text-base min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="rounded-2xl w-9 h-9 flex items-center justify-center text-white bg-gradient-to-br from-indigo-500 to-sky-400 ring-1 ring-inset ring-white/25 shadow-[0_6px_16px_-4px_rgba(99,102,241,0.55)]">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className="setup-chat-online-pulse absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-zinc-900" />
                </div>
                <div className="flex flex-col leading-tight min-w-0">
                  <span className="text-sm font-semibold truncate">{t("title")}</span>
                  <span className="text-[10px] font-normal text-zinc-400 truncate flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    Online · {serviceName}
                  </span>
                </div>
                <StageBadge />
              </div>
              <div className="flex items-center gap-2">
                {/* Chat ⇄ Form. Same fields, same saves — just a wide all-open
                    view for whoever already knows the answers. */}
                <div className="flex items-center rounded-lg border border-zinc-700/80 overflow-hidden flex-shrink-0 p-0.5 gap-0.5 bg-zinc-950/60">
                  {[
                    { id: "chat", label: t("viewChat"), Icon: Sparkles },
                    { id: "form", label: t("viewForm"), Icon: ListChecks },
                  ].map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setView(v.id)}
                      title={v.id === "form" ? t("viewFormHint") : t("viewChatHint")}
                      className={
                        "flex items-center gap-1 px-2.5 h-6 rounded-md text-[11px] transition-colors " +
                        (view === v.id
                          ? "bg-gradient-to-r from-indigo-500 to-sky-400 text-white shadow-sm"
                          : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60")
                      }
                    >
                      <v.Icon className="h-3 w-3" />
                      {v.label}
                    </button>
                  ))}
                </div>
                {!isClient && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyClientLink}
                    className="h-7 px-2 text-xs text-zinc-300 hover:text-indigo-400"
                    title={t("copyLink")}
                  >
                    <LinkIcon className="h-3.5 w-3.5 mr-1" />
                    {t("copyLink")}
                  </Button>
                )}
                {state?.available_languages?.length > 0 && (
                  <select
                    value={state?.language || "en"}
                    onChange={(e) => setLanguage(e.target.value)}
                    disabled={!canEdit}
                    className="bg-zinc-950/60 border border-zinc-700/80 text-zinc-200 text-xs rounded-lg px-2 py-1 outline-none focus:border-indigo-500 mr-8"
                    title={t("languageTitle")}
                  >
                    {state.available_languages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("loading")}
            </div>
          ) : (
            <>
            {/* Gatekeeper framing — shown to the client (or a client-stage view).
                Resets responsibility gently: the team did their part, this is
                yours, give what you have, the rest we can handle. */}
            {clientTotal > 0 && (isClient || stage === "client") && (
              <div className="flex-shrink-0 px-5 py-3.5 bg-gradient-to-br from-indigo-500/12 via-transparent to-emerald-500/12 border-b border-indigo-500/15">
                <div className="text-[15px] font-semibold text-indigo-200">{t("gkBannerTitle")}</div>
                <div className="text-[12.5px] text-zinc-400 mt-0.5 max-w-2xl leading-relaxed">{t("gkBannerBody")}</div>
              </div>
            )}
            <div
              className={
                "flex-1 grid gap-0 min-h-0 " +
                (isForm ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[1fr_320px]")
              }
            >
              {/* Chat column — hidden (not unmounted) in form view so the
                  scroll position and in-flight draft survive a toggle. */}
              <div
                className={
                  "flex-col min-h-0 border-r border-zinc-800 " +
                  (isForm ? "hidden" : "flex")
                }
              >
                <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-zinc-500 text-sm">{t("noMessages")}</div>
                  )}
                  {messages.map((m, i) => {
                    const prev = messages[i - 1];
                    const isFirstOfGroup =
                      !prev || prev.role !== m.role;
                    return (
                      <Bubble
                        key={i}
                        m={m}
                        formatVal={formatVal}
                        isLast={i === messages.length - 1}
                        isFirstOfGroup={isFirstOfGroup}
                        lang={lang}
                        disabled={!canEdit || sending}
                        onPick={(value) => sendMessage(value)}
                      />
                    );
                  })}
                  {sending && <TypingIndicator />}
                </div>

                {/* Intake panel — pre-fill from a brief / website. Shows only at
                    the very start of sales drafting; auto-hides on first action. */}
                {intakePanelVisible && (
                  <div className="border-t border-zinc-800 bg-indigo-500/[0.06] p-3 space-y-2 flex-shrink-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-indigo-300">
                          ⚡ {t("intakeTitle")}
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">
                          {t("intakeBlurb")}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIntakeDismissed(true)}
                        className="text-zinc-500 hover:text-zinc-300 text-xs flex-shrink-0"
                        title={t("intakeSkip")}
                      >
                        ×
                      </button>
                    </div>
                    {!intakeUrlOpen ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => triggerUpload(null)}
                          disabled={uploadingFile}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs"
                        >
                          {uploadingFile ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Paperclip className="h-3.5 w-3.5 mr-1" />
                          )}
                          {t("intakeUploadFile")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIntakeUrlOpen(true)}
                          className="bg-transparent border-indigo-400/50 text-indigo-300 hover:bg-indigo-500/10 text-xs"
                        >
                          <LinkIcon className="h-3.5 w-3.5 mr-1" />
                          {t("intakePasteUrl")}
                        </Button>
                        <button
                          type="button"
                          onClick={() => setIntakeDismissed(true)}
                          className="text-xs text-zinc-500 hover:text-zinc-300 underline-offset-2 hover:underline px-1"
                        >
                          {t("intakeSkip")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="url"
                          value={intakeUrlInput}
                          onChange={(e) => setIntakeUrlInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && intakeUrlInput.trim()) {
                              const url = intakeUrlInput.trim();
                              setIntakeUrlInput("");
                              setIntakeUrlOpen(false);
                              setIntakeDismissed(true);
                              sendMessage(url);
                            }
                            if (e.key === "Escape") {
                              setIntakeUrlInput("");
                              setIntakeUrlOpen(false);
                            }
                          }}
                          placeholder={t("intakeUrlPlaceholder")}
                          autoFocus
                          className="flex-1 px-3 py-1.5 rounded-lg bg-zinc-950/60 border border-indigo-700/50 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/15 outline-none text-sm text-zinc-50 transition-colors"
                        />
                        <Button
                          size="sm"
                          disabled={!intakeUrlInput.trim() || sending}
                          onClick={() => {
                            const url = intakeUrlInput.trim();
                            if (!url) return;
                            setIntakeUrlInput("");
                            setIntakeUrlOpen(false);
                            setIntakeDismissed(true);
                            sendMessage(url);
                          }}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs"
                        >
                          {t("intakeUrlSubmit")}
                        </Button>
                        <button
                          type="button"
                          onClick={() => {
                            setIntakeUrlInput("");
                            setIntakeUrlOpen(false);
                          }}
                          className="text-zinc-500 hover:text-zinc-300 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Input + actions */}
                {stage !== "completed" && (
                  <div className="border-t border-zinc-800 p-3 space-y-2 flex-shrink-0">
                    {pendingAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {pendingAttachments.map((a, i) => {
                          const v = a.validation || {};
                          const failed = v.ok === false;
                          const passed = v.ok === true && !v.skipped;
                          return (
                            <span
                              key={i}
                              title={v.message || ""}
                              className={
                                "inline-flex items-center gap-1 text-xs rounded px-2 py-0.5 border " +
                                (failed
                                  ? "bg-red-950/40 border-red-700 text-red-200"
                                  : passed
                                  ? "bg-emerald-950/30 border-emerald-700 text-emerald-200"
                                  : "bg-zinc-800 border-zinc-700 text-zinc-200")
                              }
                            >
                              <Paperclip className="h-3 w-3" />
                              {a.file_name}
                              {a.field && (
                                <span className={failed ? "text-red-300" : "text-indigo-400"}>
                                  → {a.field}
                                </span>
                              )}
                              {failed && <span className="text-red-300">⚠</span>}
                              {passed && <span className="text-emerald-300">✓</span>}
                              <button
                                onClick={() =>
                                  setPendingAttachments((p) => p.filter((_, x) => x !== i))
                                }
                                className="text-zinc-500 hover:text-red-400 ml-1"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                        {pendingAttachments.some((a) => a.validation?.ok === false) && (
                          <div className="basis-full text-[11px] text-amber-400 pt-1">
                            {pendingAttachments
                              .filter((a) => a.validation?.ok === false)
                              .map((a, i) => (
                                <div key={i}>
                                  <span className="font-medium">⚠ {a.file_name}:</span> {a.validation?.message}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => triggerUpload(null)}
                        disabled={uploadingFile || !canEdit}
                        className="h-9 w-9 p-0 text-zinc-400 hover:text-indigo-400"
                        title={t("attach")}
                      >
                        {uploadingFile ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Paperclip className="h-4 w-4" />
                        )}
                      </Button>
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          // Enter sends; Shift+Enter inserts a newline.
                          if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
                            e.preventDefault();
                            if (draft.trim() || pendingAttachments.length > 0) {
                              sendMessage();
                            }
                          }
                        }}
                        placeholder={
                          stage === "sales"
                            ? t("placeholderSales")
                            : t("placeholderClient")
                        }
                        rows={2}
                        disabled={!canEdit || sending}
                        className="flex-1 px-3 py-2 rounded-xl bg-zinc-950/60 border border-zinc-700/70 focus:border-indigo-500/70 focus:ring-2 focus:ring-indigo-500/15 outline-none text-sm resize-none placeholder:text-zinc-600 transition-colors"
                      />
                      <Button
                        size="sm"
                        onClick={() => sendMessage()}
                        disabled={
                          !canEdit ||
                          sending ||
                          (!draft.trim() && pendingAttachments.length === 0)
                        }
                        className="h-9 w-9 p-0 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-400 hover:from-indigo-400 hover:to-sky-300 text-white shadow-[0_6px_16px_-4px_rgba(99,102,241,0.6)] disabled:shadow-none disabled:opacity-50 transition-all"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-2 px-1">
                      <div className="text-[10px] text-zinc-500">
                        {t("inputHint")}
                      </div>
                      {/* Skip the current question and move the cursor forward.
                          Works for required fields too: they're mandatory for the
                          CLIENT's final review, not for the rep's internal drafting,
                          so skipping just leaves the field unfilled (the "send to
                          client" gate still warns). Backend honors skipped required
                          fields in _setup_next_step + /save-field.

                          The exception is sales-owned required fields: those gate
                          the PM-pool handoff, so the button is hidden for them and
                          /save-field rejects the skip server-side too. */}
                      {canEdit &&
                        state?.next_step?.type === "ask" &&
                        state?.next_step?.field &&
                        !(state?.sales_required_fields || []).includes(state.next_step.field) && (
                          <button
                            type="button"
                            disabled={sending || savingField === state.next_step.field}
                            onClick={() =>
                              saveField(state.next_step.field, null, { skipped: true })
                            }
                            className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-indigo-300 whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                            title={t("chatSkip")}
                          >
                            {savingField === state.next_step.field ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <SkipForward className="h-3 w-3" />
                            )}
                            {t("chatSkip")}
                          </button>
                        )}
                    </div>
                  </div>
                )}

                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  onChange={handleFile}
                />
              </div>

              {/* Progress + actions panel. In chat view it's the 320px right
                  sidebar (column: header on top, field list below). In form
                  view the field list becomes the main form on the LEFT and this
                  panel stays a 320px sidebar on the RIGHT — same familiar shell,
                  just chat swapped for the form. Achieved by flipping this
                  container to a row (list 1fr | panel 320px) instead of moving
                  any DOM. */}
              <div className={"flex min-h-0 bg-zinc-950/50 " + (isForm ? "flex-row-reverse" : "flex-col")}>
                <div
                  className={
                    "flex-shrink-0 space-y-2 px-4 py-3 " +
                    (isForm
                      ? "w-[320px] border-l border-zinc-800 overflow-y-auto"
                      : "border-b border-zinc-800")
                  }
                >
                  <div className="flex items-center justify-between text-xs">
                    <span
                      className="text-zinc-400 truncate"
                      title={state?.flow_id ? t("flowTooltip", state.flow_id) : ""}
                    >
                      {t("flowLabel")}: <span className="text-zinc-200">{state?.flow_name || t("flowDefault")}</span>
                      {!isClient && state?.flow_id && (
                        <span className="text-zinc-600 font-mono ml-1">({state.flow_id})</span>
                      )}
                    </span>
                    <span className="font-mono text-indigo-400">
                      {clientTotal > 0 ? `${clientPct}%` : `${progressPct}% · ${totalRatio}`}
                    </span>
                  </div>

                  {clientTotal > 0 ? (
                    /* Gatekeeper progress — the client's part is ranked, so show
                       it by tier and reward top-down filling. */
                    <div className="space-y-2">
                      <div
                        className={
                          "text-[11px] font-medium leading-snug " +
                          (essentialsDone ? "text-emerald-400" : "text-zinc-300")
                        }
                      >
                        {essentialsDone && !allClientDone && "✓ "}
                        {allClientDone && "🚀 "}
                        {gateHeadline}
                      </div>
                      {[
                        { key: "e", d: essential, label: t("gkEssential"), bar: "bg-emerald-500", dot: "text-emerald-400" },
                        { key: "h", d: helpful, label: t("gkHelpful"), bar: "bg-sky-500", dot: "text-sky-400" },
                        { key: "o", d: optional, label: t("gkOptional"), bar: "bg-zinc-500", dot: "text-zinc-400" },
                      ].filter((r) => r.d.total > 0).map((r) => (
                        <div key={r.key} className="space-y-0.5">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className={r.dot}>{r.label}</span>
                            <span className="font-mono text-zinc-500">{r.d.filled}/{r.d.total}</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800/80 rounded-full overflow-hidden">
                            <div
                              className={"h-full rounded-full transition-all duration-500 " + r.bar}
                              style={{ width: `${r.d.total ? (r.d.filled * 100) / r.d.total : 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                      {improvisableMissing.length > 0 && (
                        <div className="text-[10px] text-violet-300/80 flex items-start gap-1 pt-0.5">
                          <Wand2 className="h-3 w-3 flex-shrink-0 mt-0.5" />
                          {t("gkImprovise", improvisableMissing.length)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="h-1.5 bg-zinc-800/80 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-sky-400 rounded-full transition-all duration-500"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-zinc-500 flex items-center justify-between">
                        <span>{t("requiredLabel")} {requiredFilledRatio}</span>
                        {state?.sent_to_client_at && (
                          <span>{t("sentPrefix")} {state.sent_to_client_at.slice(0, 10)}</span>
                        )}
                      </div>
                    </>
                  )}

                  {/* Stage actions — a HANDOFF, not a save. Labelled + divided so
                      the team stops reading "Send to client" as the save button. */}
                  {!isClient && canEdit && (stage === "sales" || stage === "client") && (
                    <div className="pt-1 mt-1 border-t border-zinc-800 text-[10px] uppercase tracking-wide text-zinc-500">
                      {t("handoffLabel")}
                    </div>
                  )}
                  {!isClient && stage === "sales" && canEdit && (
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!state?.all_required_filled) {
                          if (!window.confirm(t("confirmSendIncomplete"))) return;
                        }
                        setStage("client");
                      }}
                      className="w-full bg-gradient-to-r from-indigo-500 to-sky-400 hover:from-indigo-400 hover:to-sky-300 text-white rounded-xl shadow-[0_8px_22px_-8px_rgba(99,102,241,0.6)]"
                    >
                      <Users className="h-3.5 w-3.5 mr-1.5" />
                      {t("sendToClient")}
                      <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  )}
                  {/* Ping the project PM to review the brief before it goes to
                      the client. Sits right under "Send to client". */}
                  {!isClient && stage === "sales" && canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={notifyManager}
                      disabled={notifyingManager}
                      className="w-full bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      {notifyingManager ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <BellRing className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      {t("notifyManager")}
                    </Button>
                  )}
                  {!isClient && stage === "client" && canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setStage("sales")}
                      className="w-full bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      {t("backToSales")}
                    </Button>
                  )}
                  {stage === "completed" && (
                    <div className="flex items-center gap-1.5 text-emerald-400 text-xs">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t("allRequiredCollected")}
                    </div>
                  )}
                  {!isClient && (
                    <div className="text-[10px] text-zinc-500 italic px-1 pt-1">
                      {t("settings")}
                    </div>
                  )}
                </div>

                {/* Schema list with answer status. Click any row to expand it
                    and see the full saved value. Status icons:
                      ✓ verified (green/cyan)
                      ◐ filled but not verified yet
                      ⊘ skipped (optional, user dismissed it)
                      ◯ unanswered
                      ▶ current cursor (what AI is asking next)
                */}
                <div className="flex-1 min-w-0 overflow-y-auto px-3.5 py-2.5 space-y-3.5">
                  {/* Reassure that filling = saving. The sales team read the
                      handoff buttons as "save"; make it explicit there's no
                      separate save step. */}
                  {isForm && (
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-400/90 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      {t("autoSaveHint")}
                    </div>
                  )}
                  {sections.map(([section, items]) => {
                    const verifiedSet = new Set(state?.verified_fields || []);
                    const skippedSet = new Set(state?.skipped_fields || []);
                    const cursorField = state?.next_step?.field;
                    const filledCount = items.filter((q) => responses[q.field]).length;
                    const allDone = filledCount === items.length && items.length > 0;
                    return (
                      <div key={section}>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 px-0.5 mb-1.5 flex items-center justify-between border-b border-zinc-800/70 pb-1">
                          <span className="flex items-center gap-1.5">
                            {allDone && <CheckCircle2 className="h-3 w-3 text-emerald-400/80" />}
                            {section}
                          </span>
                          <span className={"font-mono " + (allDone ? "text-emerald-400/80" : "text-zinc-600")}>
                            {filledCount}/{items.length}
                          </span>
                        </div>
                        <ul className="space-y-0.5">
                          {items.map((q) => {
                            const v = responses[q.field];
                            const filled = !!v;
                            const verified = filled && verifiedSet.has(q.field);
                            const skipped = !filled && skippedSet.has(q.field);
                            const isCursor = q.field === cursorField;
                            // Priority tier drives the row's colour so importance
                            // reads per-field, matching the bars up top. Sales gate
                            // fields keep the amber "you" treatment instead.
                            const isSalesGate = (state?.sales_required_fields || []).includes(q.field);
                            const tierMeta = {
                              essential: { dot: "text-emerald-400", ring: "border-emerald-500/60", chip: "text-emerald-400/90 bg-emerald-500/10", label: t("gkEssential") },
                              helpful: { dot: "text-sky-400", ring: "border-sky-500/50", chip: "text-sky-400/90 bg-sky-500/10", label: t("gkHelpful") },
                              optional: { dot: "text-zinc-500", ring: "border-zinc-600", chip: "text-zinc-400 bg-zinc-500/10", label: t("gkOptional") },
                            }[q.priority] || null;
                            // Form view auto-opens only rows that still need input;
                            // filled rows collapse to a compact one-line summary
                            // (click to edit) so the list stays dense and progress
                            // feels rewarding. Chat view opens one row at a time.
                            const isExpanded = isForm
                              ? (!filled || expandedField === q.field)
                              : expandedField === q.field;
                            const fileVal = filled && typeof v === "object" && v.file_url ? v : null;
                            const textVal = filled && !fileVal && !Array.isArray(v) ? String(v) : "";
                            // Types that get a real textbox in the expanded body —
                            // for those the saved value is already editable in place,
                            // so no read-only copy of it above. backlog c36b2393
                            const hasTextEditor =
                              canEdit &&
                              !["file", "address", "points", "yesno", "select"].includes(q.type);
                            // Always allow expand on click — every row hosts useful
                            // content when open: filled rows show the saved value, non-file
                            // rows an inline editor, and even an empty file row shows its
                            // question text + an Attach button. (Previously empty file rows
                            // were dead — the row didn't react to clicks at all.)
                            const expandable = true;
                            return (
                              <li
                                key={q.field}
                                className={
                                  "text-xs rounded-lg transition-colors " +
                                  (isCursor
                                    ? "bg-indigo-500/10 ring-1 ring-inset ring-indigo-500/40"
                                    : isExpanded
                                    ? "bg-zinc-900/40 ring-1 ring-inset ring-zinc-800/70"
                                    : filled
                                    ? "hover:bg-zinc-900/40"
                                    : "hover:bg-zinc-900/30")
                                }
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    expandable &&
                                    setExpandedField((cur) => (cur === q.field ? null : q.field))
                                  }
                                  className="w-full text-left px-2 py-1 flex items-start gap-2"
                                  title={
                                    isCursor
                                      ? t("statusCursor")
                                      : verified
                                      ? t("statusVerified")
                                      : filled
                                      ? t("statusFilled")
                                      : skipped
                                      ? t("statusSkipped")
                                      : t("statusUnasked")
                                  }
                                >
                                  {isCursor ? (
                                    <span className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-indigo-400 leading-none">▶</span>
                                  ) : verified ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0 mt-0.5" />
                                  ) : filled ? (
                                    <span className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-400 leading-none text-[14px]">◐</span>
                                  ) : skipped ? (
                                    <span className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-zinc-500 leading-none text-[14px]">⊘</span>
                                  ) : tierMeta ? (
                                    <span
                                      className={"h-3 w-3 mt-0.5 flex-shrink-0 rounded-full border-2 bg-transparent " + tierMeta.ring}
                                    />
                                  ) : (
                                    <Circle className="h-3.5 w-3.5 text-zinc-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className={`truncate min-w-0 ${
                                          filled ? "text-zinc-200" : "text-zinc-400"
                                        }`}
                                      >
                                        {q.label}
                                        {q.required && (
                                          <span className="text-red-400 ml-1">*</span>
                                        )}
                                      </span>
                                      {/* Sales-owned gate field — kept out of the
                                          truncating label so its badge never clips. */}
                                      {!isClient && isSalesGate && !filled && (
                                        <span
                                          className="flex-shrink-0 text-[9px] px-1 rounded border border-amber-500/40 text-amber-300 bg-amber-950/30"
                                          title={t("blocksPoolHint")}
                                        >
                                          {t("blocksPool")}
                                        </span>
                                      )}
                                      {/* Per-field priority — the importance the
                                          client asked to see on each row. flex-shrink-0
                                          so the tier reads in full; the label truncates. */}
                                      {tierMeta && !filled && !isSalesGate && (
                                        <span className={"flex-shrink-0 text-[9px] px-1.5 py-px rounded-full " + tierMeta.chip}>
                                          {tierMeta.label}
                                        </span>
                                      )}
                                      {q.can_improvise && !filled && (
                                        <Wand2 className="flex-shrink-0 h-2.5 w-2.5 text-violet-400/70" title={t("gkImproviseField")} />
                                      )}
                                      {skipped && (
                                        <span className="flex-shrink-0 text-zinc-500 text-[10px]">· {t("skippedSuffix")}</span>
                                      )}
                                    </div>
                                    {filled && !isExpanded && (
                                      <div className="text-zinc-500 truncate">
                                        {formatVal(v)}
                                      </div>
                                    )}
                                  </div>
                                  {expandable && (
                                    <span className="text-zinc-600 text-[10px] flex-shrink-0 mt-0.5">
                                      {isExpanded ? "▾" : "▸"}
                                    </span>
                                  )}
                                </button>
                                {/* Expanded body: shows the saved value + an inline editor.
                                    For files we keep the existing read-only view + Attach
                                    button. Everything else gets a type-appropriate input. */}
                                {isExpanded && (
                                  <div className="px-2.5 pb-1.5 pt-0 space-y-1">
                                    {q.message && !filled && (
                                      <div className="text-[11px] text-zinc-500 leading-snug">{q.message}</div>
                                    )}
                                    {fileVal && (
                                      <a
                                        href={fileVal.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-400 hover:underline inline-flex items-center gap-1 break-all"
                                      >
                                        {fileVal.is_link ? (
                                          <LinkIcon className="h-3 w-3 flex-shrink-0" />
                                        ) : (
                                          <Paperclip className="h-3 w-3 flex-shrink-0" />
                                        )}
                                        {fileVal.file_name}
                                      </a>
                                    )}
                                    {/* Read-only echo of the saved value. Skipped when
                                        an editable textbox is rendered right below —
                                        showing both made the answer look locked: users
                                        tried to type into this box, couldn't, and
                                        reported "filled fields can't be edited".
                                        backlog c36b2393 */}
                                    {filled && !fileVal && !hasTextEditor && q.type !== "address" && q.type !== "points" && (
                                      <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[11px] text-zinc-200 whitespace-pre-wrap break-words">
                                        {textVal || (
                                          <span className="text-zinc-600 italic">{t("emptyValue")}</span>
                                        )}
                                      </div>
                                    )}
                                    {!filled && skipped && (
                                      <div className="text-[11px] text-zinc-500 italic">
                                        {t("skippedNote")}
                                      </div>
                                    )}
                                    {/* Points of interest — a map with OSM auto-suggestions;
                                        tick nearby places, click to add your own. */}
                                    {canEdit && q.type === "points" && (
                                      <SetupPointsField
                                        q={q}
                                        api={api}
                                        taskId={taskId}
                                        currentValue={Array.isArray(v) ? v : []}
                                        t={t}
                                        saving={savingField === q.field}
                                        onSavePoints={(points) => savePoints(q.field, points)}
                                      />
                                    )}
                                    {/* Address gets a map with a draggable marker instead
                                        of a plain box — type/confirm, then fine-tune the pin. */}
                                    {canEdit && q.type === "address" && (
                                      <SetupAddressField
                                        q={q}
                                        currentValue={textVal}
                                        meta={state?.address_meta?.[q.field]}
                                        t={t}
                                        saving={savingField === q.field}
                                        onSave={(val) => saveField(q.field, val, { keepOpen: true })}
                                        onSaveCoords={(lat, lng) => saveCoords(q.field, lat, lng)}
                                        onClear={
                                          filled
                                            ? () => saveField(q.field, null, { skipped: false, keepOpen: true })
                                            : null
                                        }
                                      />
                                    )}
                                    {/* Inline editor: write/select/yesno/url */}
                                    {canEdit && q.type !== "file" && q.type !== "address" && q.type !== "points" && (
                                      <FieldInlineEditor
                                        q={q}
                                        currentValue={fileVal ? null : textVal}
                                        suggestion={state?.suggestions?.[q.field]?.value}
                                        lang={lang}
                                        t={t}
                                        saving={savingField === q.field}
                                        onSave={(val) => saveField(q.field, val)}
                                        onSkip={!q.required ? () => saveField(q.field, null, { skipped: true }) : null}
                                        onClear={
                                          filled
                                            ? () => saveField(q.field, null, { skipped: false, keepOpen: true })
                                            : null
                                        }
                                      />
                                    )}
                                    {/* File field editor: upload a file OR paste a link.
                                        A link carries the SAME weight as an uploaded file. */}
                                    {q.type === "file" && canEdit && (
                                      <div className="space-y-1.5">
                                        <div className="flex items-center gap-3">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              triggerUpload(q.field);
                                            }}
                                            disabled={uploadingFile}
                                            className="text-indigo-400 hover:underline text-[11px] inline-flex items-center gap-1"
                                          >
                                            <Paperclip className="h-3 w-3" />
                                            {fileVal ? t("replaceFile") : t("attach")}
                                          </button>
                                          {filled && (
                                            <ClearAnswerButton
                                              t={t}
                                              disabled={savingField === q.field}
                                              onClick={() =>
                                                saveField(q.field, null, { skipped: false, keepOpen: true })
                                              }
                                            />
                                          )}
                                        </div>
                                        {/* A saved LINK answer is editable in place —
                                            it used to render read-only above an empty
                                            box, so the only way to correct a typo was
                                            clear-then-retype. backlog c36b2393 */}
                                        <FileLinkEditor
                                          t={t}
                                          saving={savingField === q.field}
                                          currentUrl={fileVal?.is_link ? fileVal.file_url : ""}
                                          onSave={(url) => saveField(q.field, url, { keepOpen: true })}
                                        />
                                      </div>
                                    )}
                                    {filled && (
                                      <div className="text-[10px] text-zinc-600">
                                        {verified ? t("confirmedHint") : t("awaitingHint")}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* File upload affordance for empty file fields when collapsed */}
                                {!isExpanded && q.type === "file" && !filled && canEdit && (
                                  <div className="px-2 pb-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        triggerUpload(q.field);
                                      }}
                                      disabled={uploadingFile}
                                      className="text-indigo-400 hover:underline text-[11px]"
                                    >
                                      {t("attach")}
                                    </button>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            </>
          )}
    </>
  );

  // In-page (no launcher, no overlay) — the setup lives right in the cabinet.
  if (inline) {
    return (
      <div
        className="flex flex-col rounded-2xl border border-zinc-800 bg-zinc-950 text-zinc-50 overflow-hidden shadow-[0_20px_50px_-25px_rgba(30,40,90,0.35)]"
        style={{ height: "min(80vh, 780px)" }}
      >
        {chatShell}
      </div>
    );
  }

  return (
    <>
      {variant === "button" ? (
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-500 text-white"
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          {t("title")}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(true)}
          className="h-6 w-6 p-0 text-zinc-400 hover:text-indigo-400"
          title={t("title")}
        >
          <MessageCircle className="h-3.5 w-3.5" />
        </Button>
      )}

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          // Tell the host page on close, but only if something was actually
          // written — otherwise every peek at the chat refetches the gate.
          if (!v && dirtyRef.current) {
            dirtyRef.current = false;
            onSaved?.();
          }
        }}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-5xl w-[95vw] h-[85vh] p-0 overflow-hidden flex flex-col rounded-2xl shadow-[0_40px_80px_-30px_rgba(30,40,90,0.55)]">
          <DialogTitle className="sr-only">{t("title")}</DialogTitle>
          {chatShell}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Single chat bubble ─────────────────────────────────────────────────────

// Localised yes/no labels for the cursor-button widget. The backend emits
// __YES__ / __NO__ markers; we render them in the active chat language.
const YESNO_LABEL = {
  en: { __YES__: "Yes", __NO__: "No" },
  uk: { __YES__: "Так", __NO__: "Ні" },
  ru: { __YES__: "Да", __NO__: "Нет" },
};

// One-time CSS injection for keyframes the bubbles + typing indicator use.
// Tailwind doesn't ship these by default and adding to tailwind.config takes
// a rebuild; we keep it scoped here so the modal stays self-contained.
function ensureSetupChatStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("setup-chat-anim-styles")) return;
  const el = document.createElement("style");
  el.id = "setup-chat-anim-styles";
  el.textContent = `
    @keyframes setupChatBubbleIn {
      from { opacity: 0; transform: translateY(6px) scale(0.985); }
      to   { opacity: 1; transform: translateY(0)   scale(1); }
    }
    @keyframes setupChatTypingDot {
      0%, 60%, 100% { transform: translateY(0);   opacity: 0.45; }
      30%           { transform: translateY(-3px); opacity: 1;    }
    }
    @keyframes setupChatPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,.6); }
      50%      { box-shadow: 0 0 0 4px rgba(16,185,129,0); }
    }
    .setup-chat-bubble-in { animation: setupChatBubbleIn .22s ease-out; }
    .setup-chat-typing-dot { animation: setupChatTypingDot 1.1s infinite ease-in-out; }
    .setup-chat-online-pulse { animation: setupChatPulse 1.8s infinite ease-out; }
  `;
  document.head.appendChild(el);
}

function ChatAvatar({ role, userName, size = 28 }) {
  const dim = `${size}px`;
  if (role === "user") {
    const initial = (userName || "U").trim().slice(0, 1).toUpperCase();
    return (
      <div
        className="rounded-lg flex items-center justify-center text-[11px] font-semibold text-zinc-200 bg-zinc-700 ring-1 ring-inset ring-white/10 flex-shrink-0"
        style={{ width: dim, height: dim }}
      >
        {initial}
      </div>
    );
  }
  return (
    <div
      className="rounded-lg flex items-center justify-center text-indigo-50 bg-indigo-600 ring-1 ring-inset ring-white/20 shadow-sm flex-shrink-0"
      style={{ width: dim, height: dim }}
      title="Setup Assistant"
    >
      <Sparkles className="h-3.5 w-3.5" />
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 setup-chat-bubble-in">
      <ChatAvatar role="assistant" />
      <div className="rounded-2xl rounded-bl-sm bg-zinc-800/70 border border-zinc-700/50 px-3.5 py-2.5 shadow-sm">
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="setup-chat-typing-dot inline-block w-1.5 h-1.5 rounded-full bg-indigo-400"
              style={{ animationDelay: `${i * 140}ms` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Bubble({ m, formatVal, isLast, isFirstOfGroup, lang, onPick, disabled }) {
  // Make sure animation CSS is in the document before we render an animated bubble.
  if (typeof window !== "undefined") ensureSetupChatStyles();

  const isUser = m.role === "user";
  const isHandoff = m.system_event === "handoff";

  if (isHandoff) {
    return (
      <div className="setup-chat-bubble-in border border-indigo-400/40 bg-indigo-500/10 rounded-xl px-3 py-2 text-xs text-indigo-100 whitespace-pre-line">
        {m.content}
      </div>
    );
  }

  // Build button labels for cursor-attached choices on this assistant message.
  const showChoices =
    !isUser && isLast && Array.isArray(m.choices) && m.choices.length > 0 && !disabled;
  const yesnoMap = YESNO_LABEL[lang] || YESNO_LABEL.en;
  const renderLabel = (raw) => {
    if (m.choices_qtype === "yesno") return yesnoMap[raw] || raw;
    return String(raw);
  };
  const valueFor = (raw) => {
    if (m.choices_qtype === "yesno") return yesnoMap[raw] || raw;
    return String(raw);
  };

  // Friendly time stamp HH:MM (locale-formatted).
  const timeStr = (() => {
    if (!m.ts) return "";
    try {
      const d = new Date(m.ts);
      return d.toLocaleTimeString(lang === "uk" ? "uk-UA" : lang === "ru" ? "ru-RU" : "en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  })();

  // Layout: avatar on the side of the bubble matching the role; only show on the
  // FIRST of a same-role run so back-to-back bot/user messages feel grouped.
  return (
    <div
      className={`setup-chat-bubble-in flex items-end gap-2 ${
        isUser ? "justify-end" : "justify-start"
      }`}
    >
      {!isUser && (
        <div className={isFirstOfGroup ? "" : "invisible"}>
          <ChatAvatar role="assistant" />
        </div>
      )}
      <div className="flex flex-col max-w-[78%]">
        {isFirstOfGroup && (
          <div
            className={`text-[10px] uppercase tracking-wide mb-0.5 px-1 ${
              isUser ? "text-right text-indigo-300" : "text-indigo-400"
            }`}
          >
            {isUser
              ? `${m.user_name || "You"}${
                  m.user_role && m.user_role !== "client" ? " · " + m.user_role : ""
                }`
              : "Setup Assistant"}
          </div>
        )}
        <div
          className={`text-sm leading-relaxed whitespace-pre-line transition-shadow ${
            isUser
              ? "bg-gradient-to-br from-indigo-500 to-sky-400 text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 shadow-[0_4px_14px_-4px_rgba(99,102,241,0.5)]"
              : "bg-zinc-800/70 border border-zinc-700/50 text-zinc-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm"
          }`}
        >
          <div>{m.content}</div>
          {showChoices && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {m.choices.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPick && onPick(valueFor(opt))}
                  className="px-3 py-1 text-xs rounded-full border border-indigo-400/40 bg-indigo-500/10 hover:bg-indigo-500/20 hover:border-indigo-400/60 text-indigo-100 transition-colors"
                >
                  {renderLabel(opt)}
                </button>
              ))}
            </div>
          )}
          {m.attachments && m.attachments.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {m.attachments.map((a, i) => {
                const v = a.validation || {};
                const failed = v.ok === false;
                const passed = v.ok === true && !v.skipped;
                return (
                  <div key={i} className="text-xs">
                    <a
                      href={a.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={"underline " + (isUser ? "text-indigo-100" : "text-indigo-400")}
                    >
                      📎 {a.file_name}
                    </a>
                    {a.field && <span className="opacity-70"> → {a.field}</span>}
                    {failed && <span className="ml-1 text-red-300" title={v.message || ""}>⚠ rejected</span>}
                    {passed && <span className="ml-1 text-emerald-300" title={v.message || ""}>✓ ok</span>}
                    {failed && v.message && (
                      <div className="text-[11px] text-red-300 opacity-90 ml-4">{v.message}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {m.extracted && Object.keys(m.extracted).length > 0 && (
            <div className={`mt-2 pt-2 border-t space-y-0.5 ${
              isUser ? "border-indigo-300/40" : "border-zinc-700/60"
            }`}>
              {Object.entries(m.extracted).map(([k, v]) => (
                <div
                  key={k}
                  className={`text-[11px] ${isUser ? "text-indigo-50" : "text-zinc-400"}`}
                >
                  <span className={isUser ? "text-indigo-100" : "text-indigo-400"}>✓ {k}:</span>{" "}
                  {formatVal(v)}
                </div>
              ))}
            </div>
          )}
        </div>
        {timeStr && (
          <div
            className={`text-[10px] text-zinc-500 mt-0.5 px-1 ${
              isUser ? "text-right" : "text-left"
            }`}
          >
            {timeStr}
          </div>
        )}
      </div>
      {isUser && (
        <div className={isFirstOfGroup ? "" : "invisible"}>
          <ChatAvatar role="user" userName={m.user_name} />
        </div>
      )}
    </div>
  );
}

// ── Link input for a file field ──────────────────────────────────────────
// A pasted link carries the SAME weight as an uploaded file. Sends the URL to
// /save-field, which stores it as a file-shaped {file_url, file_name, is_link}.
// `currentUrl` seeds the box when the saved answer IS a link, so an existing
// link can be corrected in place instead of cleared and re-pasted.
function FileLinkEditor({ t, saving, onSave, currentUrl = "" }) {
  const [url, setUrl] = useState(currentUrl);
  useEffect(() => {
    setUrl(currentUrl);
  }, [currentUrl]);
  const dirty = url.trim() !== (currentUrl || "").trim();
  const submit = () => {
    const v = url.trim();
    if (!v || !dirty) return;
    onSave(v);
    if (!currentUrl) setUrl("");
  };
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") setUrl(currentUrl);
        }}
        placeholder={currentUrl ? t("editLinkPlaceholder") : t("linkPlaceholder")}
        className="flex-1 min-w-0 px-2 py-1 rounded bg-zinc-950 border border-zinc-700 focus:border-indigo-500 outline-none text-[11px] text-zinc-50"
      />
      <Button
        size="sm"
        disabled={!url.trim() || !dirty || saving}
        onClick={submit}
        className="h-7 px-2 bg-indigo-600 hover:bg-indigo-500 text-white text-[11px]"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : t("saveLinkBtn")}
      </Button>
    </div>
  );
}

// Deleting an answer is a real action, not a footnote — the old muted grey text
// link read as decoration and users concluded filled fields couldn't be cleared.
// backlog c36b2393
function ClearAnswerButton({ t, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={t("clearAnswerHint")}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-rose-400/90 border border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/15 hover:text-rose-300 disabled:opacity-50 transition-colors"
    >
      <Trash2 className="h-3 w-3" />
      {t("clearAnswerBtn")}
    </button>
  );
}

// ── Inline editor for a single sidebar field ─────────────────────────────
// Renders a type-appropriate input (text/textarea/url/select/yesno/address)
// and a Save/Skip/Clear row. Calls onSave(value) which goes straight to
// the /save-field backend endpoint — no LLM round trip. yesno/select are
// committed on click since there's nothing to "Save" beyond the choice.
function FieldInlineEditor({ q, currentValue, suggestion, lang, t, saving, onSave, onSkip, onClear }) {
  // Empty field with a pre-fill hint → seed the input with the suggestion so
  // the manager confirms (Save) instead of typing. A real saved value always
  // wins over the suggestion.
  const seed = currentValue || suggestion || "";
  const [draft, setDraft] = useState(seed);
  const usingSuggestion = !currentValue && !!suggestion && draft === suggestion;

  // Reset draft when the row's saved value or suggestion changes.
  useEffect(() => {
    setDraft(currentValue || suggestion || "");
  }, [currentValue, suggestion, q.field]);

  const yesnoMap = YESNO_LABEL[lang] || YESNO_LABEL.en;
  const yesText = yesnoMap.__YES__;
  const noText = yesnoMap.__NO__;

  const dirty = (draft || "") !== (currentValue || "");
  const canSubmit = dirty && !saving && (draft || "").trim().length > 0;

  if (q.type === "yesno") {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {[yesText, noText].map((opt) => {
          const active = (currentValue || "").trim().toLowerCase() === opt.toLowerCase();
          return (
            <button
              key={opt}
              type="button"
              disabled={saving}
              onClick={() => onSave(opt)}
              className={
                "px-3 py-1 text-xs rounded-full border transition-colors " +
                (active
                  ? "bg-indigo-500/30 border-indigo-300/60 text-indigo-50"
                  : "bg-indigo-500/10 border-indigo-400/40 text-indigo-100 hover:bg-indigo-500/20")
              }
            >
              {opt}
            </button>
          );
        })}
        {onClear && (
          <span className="ml-1">
            <ClearAnswerButton t={t} disabled={saving} onClick={onClear} />
          </span>
        )}
        {onSkip && (
          <button
            type="button"
            disabled={saving}
            onClick={onSkip}
            className="text-[11px] text-zinc-500 hover:text-zinc-200 ml-1"
          >
            {t("skipFieldBtn")}
          </button>
        )}
      </div>
    );
  }

  if (q.type === "select") {
    const options = Array.isArray(q.options) ? q.options : [];
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {options.map((opt) => {
          const active = (currentValue || "").trim().toLowerCase() === String(opt).trim().toLowerCase();
          return (
            <button
              key={opt}
              type="button"
              disabled={saving}
              onClick={() => onSave(opt)}
              className={
                "px-3 py-1 text-xs rounded-full border transition-colors " +
                (active
                  ? "bg-indigo-500/30 border-indigo-300/60 text-indigo-50"
                  : "bg-indigo-500/10 border-indigo-400/40 text-indigo-100 hover:bg-indigo-500/20")
              }
            >
              {opt}
            </button>
          );
        })}
        {onClear && (
          <span className="ml-1">
            <ClearAnswerButton t={t} disabled={saving} onClick={onClear} />
          </span>
        )}
        {onSkip && (
          <button
            type="button"
            disabled={saving}
            onClick={onSkip}
            className="text-[11px] text-zinc-500 hover:text-zinc-200 ml-1"
          >
            {t("skipFieldBtn")}
          </button>
        )}
      </div>
    );
  }

  // text / textarea / url / address fall through to a textbox + buttons.
  const isMultiline = q.type === "textarea" || (q.placeholder || "").length > 60;
  const inputProps = {
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    placeholder: q.placeholder || t("answerInline"),
    disabled: saving,
    className:
      "w-full px-3 py-1 rounded-lg bg-zinc-950/60 border border-zinc-700/70 focus:border-indigo-500/70 focus:ring-2 focus:ring-indigo-500/15 outline-none text-[12px] text-zinc-50 placeholder:text-zinc-600 transition-colors",
  };

  // Compact inline save — an icon button that sits at the trailing edge of the
  // input and only appears once there's something worth saving. Enter saves too,
  // so an always-on Save button just added weight to every field.
  const SaveAffordance = () =>
    canSubmit ? (
      <Button
        size="sm"
        onClick={() => onSave(draft.trim())}
        title={usingSuggestion ? t("confirmBtn") : t("saveBtn")}
        className="h-8 w-8 p-0 flex-shrink-0 bg-gradient-to-br from-indigo-500 to-sky-400 hover:from-indigo-400 hover:to-sky-300 text-white rounded-lg shadow-[0_4px_12px_-4px_rgba(99,102,241,0.6)]"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
    ) : null;

  return (
    <div className="space-y-1.5">
      {isMultiline ? (
        <div className="space-y-1.5">
          <textarea
            {...inputProps}
            rows={3}
            onBlur={() => { if (canSubmit) onSave(draft.trim()); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (canSubmit) onSave(draft.trim());
              }
              if (e.key === "Escape") setDraft(currentValue || "");
            }}
            className={inputProps.className + " resize-y min-h-[64px]"}
          />
          {canSubmit && (
            <Button
              size="sm"
              onClick={() => onSave(draft.trim())}
              className="h-8 px-3 bg-gradient-to-r from-indigo-500 to-sky-400 hover:from-indigo-400 hover:to-sky-300 text-white text-xs rounded-lg shadow-[0_4px_12px_-4px_rgba(99,102,241,0.6)]"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              {saving ? t("saving") : usingSuggestion ? t("confirmBtn") : t("saveBtn")}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <input
            {...inputProps}
            type={q.type === "url" ? "url" : "text"}
            onBlur={() => { if (canSubmit) onSave(draft.trim()); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (canSubmit) onSave(draft.trim());
              }
              if (e.key === "Escape") setDraft(currentValue || "");
            }}
          />
          <SaveAffordance />
        </div>
      )}
      {usingSuggestion && (
        <div className="text-[10px] text-indigo-400/80 flex items-center gap-1">
          <Sparkles className="h-2.5 w-2.5" />
          {t("suggestedHint")}
        </div>
      )}
      {/* Secondary actions — muted links only; the primary Save lives by the input. */}
      {(dirty || (onClear && !dirty) || onSkip) && (
        <div className="flex items-center gap-2.5">
          {dirty && (
            <button
              type="button"
              disabled={saving}
              onClick={() => setDraft(currentValue || "")}
              className="text-[11px] text-zinc-500 hover:text-zinc-200"
            >
              {t("cancelBtn")}
            </button>
          )}
          {onClear && !dirty && (
            <ClearAnswerButton t={t} disabled={saving} onClick={onClear} />
          )}
          {onSkip && (
            <button
              type="button"
              disabled={saving}
              onClick={onSkip}
              className="text-[11px] text-zinc-500 hover:text-zinc-200"
            >
              {t("skipFieldBtn")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
